import Collapse from "@mui/material/Collapse";

import {
  getTruncatedPlanLabel,
  isFreePlan,
  isPlanDowngrade,
  isSamePlan,
} from "@app/analytics/account/Billing/utils";
import LINKS from "@app/common/links";
import ROUTES from "@app/common/routes";
import type {
  BillingCadence,
  BillingCurrentPlan,
  BillingPlan,
  BillingScheduledPlan,
} from "@app/common/types";
import { withQueryParams } from "@app/common/utils";
import Config from "@app/config";
import { formatCurrency } from "@app/utils/utils";

import Link from "@rui/Link";
import Stack, { Row } from "@rui/Stack";

import { OPTS_BY_PLAN } from "./constants";
import {
  Container,
  Content,
  Footer,
  Heading,
  Description,
  Disclaimer,
  Price,
  UpgradeButton,
} from "./styles";

const FREE_PRICES = {
  monthly: 0,
  annual: 0,
};

const getButtonProps = (
  { plan, cadence }: { plan: BillingPlan; cadence: BillingCadence },
  currentPlan?: BillingCurrentPlan,
  scheduledPlan?: BillingScheduledPlan
) => {
  const isFreePlanDetail = isFreePlan(plan);

  if (!currentPlan) {
    // no current plan will be passed if the user is in their trial, since they should be able to select
    // any of the plans at will (except the free plans!)
    return {
      text: isFreePlanDetail ? "" : "Upgrade",
      disabled: isFreePlanDetail,
    };
  }

  const useScheduledPlan =
    scheduledPlan != null && !isSamePlan(currentPlan, scheduledPlan);

  const planToCompare = useScheduledPlan ? scheduledPlan : currentPlan;

  if (
    (isFreePlanDetail && planToCompare.type === plan) ||
    (planToCompare.type === plan && planToCompare.billingCadence === cadence)
  ) {
    return {
      text: useScheduledPlan ? "Upcoming Plan" : "Current Plan",
      disabled: true,
    };
  }

  if (isFreePlanDetail) {
    return {
      text: "",
      disabled: true,
    };
  }

  if (
    isPlanDowngrade(planToCompare, {
      type: plan,
      billingCadence: cadence,
    })
  ) {
    return {
      text: "Select plan",
      disabled: false,
    };
  }

  return {
    text: "Upgrade",
    disabled: false,
  };
};

export interface PlanDetailProps {
  cadence: BillingCadence;
  currentPlan?: BillingCurrentPlan;
  plan: Exclude<BillingPlan, "unknown">;
  scheduledPlan?: BillingScheduledPlan;
  useLegacyPrices: boolean;
}

const PlanDetail = ({
  cadence,
  currentPlan,
  plan,
  scheduledPlan,
  useLegacyPrices,
}: PlanDetailProps) => {
  const { description, isMostPopular } = OPTS_BY_PLAN[plan];

  const prices = isFreePlan(plan)
    ? FREE_PRICES
    : useLegacyPrices
      ? Config.billing.prices.legacyByPlan[plan]
      : Config.billing.prices.byPlan[plan];

  const buttonProps = getButtonProps(
    { plan, cadence },
    currentPlan,
    scheduledPlan
  );

  const to = buttonProps.disabled
    ? undefined
    : withQueryParams(ROUTES.BILLING_CHECKOUT_SUBSCRIPTION, {
        c: cadence,
        p: plan,
      });

  const priceHasSavings = prices.annual !== prices.monthly;

  return (
    <Container isMostPopular={isMostPopular}>
      <Content>
        <Stack spacing={1.5} sx={{ maxWidth: "280px" }}>
          <Heading>{getTruncatedPlanLabel(plan)}</Heading>
          <Description>{description}</Description>
          <Stack>
            <Row>
              {priceHasSavings && (
                <Collapse orientation="horizontal" in={cadence === "annual"}>
                  <Price active={cadence === "annual"}>
                    {formatCurrency(prices.annual, {
                      digits: 0,
                      withUSD: false,
                    })}
                    &nbsp;
                  </Price>
                </Collapse>
              )}
              <Price active={cadence === "monthly" || !priceHasSavings}>
                {formatCurrency(prices.monthly ?? 0, {
                  digits: 0,
                  withUSD: false,
                })}
              </Price>
            </Row>
            {prices ? (
              <Disclaimer>USD. Per license per month.</Disclaimer>
            ) : (
              <Disclaimer>Free, no credit card required</Disclaimer>
            )}
          </Stack>
          <Link to={to} openInBrowser replace>
            <UpgradeButton
              data-test-id={`billing-select-plan-${plan}-${cadence}`}
              disabled={buttonProps.disabled}
              sx={{ visibility: buttonProps.text ? "visible" : "hidden" }}
            >
              {buttonProps.text}
            </UpgradeButton>
          </Link>
        </Stack>
      </Content>
      <Link href={LINKS.MARKETING.PLANS_PRICING}>
        <Footer />
      </Link>
    </Container>
  );
};

export default PlanDetail;
